import { useUpdate, useGetList } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

const savedToSelect = (v: string | null | undefined) =>
  v === '' || v == null ? '' : v;

const getModelName = (
  models: { id?: string; _id?: string; name: string }[],
  id: string,
) => models.find(m => (m.id ?? m._id) === id)?.name ?? id;

export const DefaultModelId = () => {
  const [updateConfig, { isLoading: isUpdating }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );
  const { remoteAppConfig, refetchAppConfig } = useGetAppConfig();
  const { onSettled: doSettled } = useOnSettled();
  const { data: modelsData } = useGetList('llmModels', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  const modelsRaw = Array.isArray(modelsData) ? modelsData : [];
  const models = modelsRaw.filter(
    (m: { purpose: string }) => m.purpose === 'processing',
  );
  const savedValue =
    (remoteAppConfig as { defaultModelId?: string | null })?.defaultModelId ??
    '';
  const savedSelect = savedToSelect(savedValue);

  const [pendingSelect, setPendingSelect] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setPendingSelect(savedSelect);
  }, [savedSelect]);

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  const handleDropdownChange = useCallback(
    (event: { target: { value: string } }) => {
      setPendingSelect(event.target.value);
    },
    [],
  );

  const handleSaveClick = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    setConfirmOpen(false);
    const value = pendingSelect === '' ? null : pendingSelect;
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: { defaultModelId: value },
        previousData: remoteAppConfig as any,
      },
      { onSettled },
    );
  }, [
    updateConfig,
    onSettled,
    remoteAppConfig,
    pendingSelect,
  ]);

  const hasChanges = pendingSelect !== savedSelect;
  const pendingName =
    pendingSelect === '' ? 'no default' : getModelName(models, pendingSelect);
  const savedName =
    savedSelect === '' ? 'no default' : getModelName(models, savedSelect);

  return (
    <Box marginTop="2rem">
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        Default LLM model
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        App-wide default model for GPT/LLM requests. Overridable per therapist.
      </Typography>
      <FormControl size="small" disabled={isUpdating} sx={{ minWidth: 220 }}>
        <InputLabel>Model</InputLabel>
        <Select
          value={pendingSelect}
          label="Model"
          onChange={handleDropdownChange}
          displayEmpty
          renderValue={v =>
            v === ''
              ? ''
              : (models.find((m: { id?: string }) => m.id === v) as
                  | { name?: string }
                  | undefined)?.name ?? v
          }
        >
          <MenuItem value=""> </MenuItem>
          {models.map((m: { id?: string; _id?: string; name: string }) => (
            <MenuItem key={m.id ?? m._id} value={m.id ?? m._id}>
              {m.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {hasChanges && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={isUpdating}
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </Box>
      )}
      {isUpdating && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption">Saving…</Typography>
        </Box>
      )}
      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Change default LLM model?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {savedSelect === ''
              ? `Set the default model to "${pendingName}"? This will be used for all GPT/LLM requests unless a therapist has a per-therapist override.`
              : pendingSelect === ''
                ? `Clear the default model (currently "${savedName}")? The app will fall back to environment configuration.`
                : `Change the default model from "${savedName}" to "${pendingName}"? This will affect all therapists without a per-therapist override.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>Cancel</Button>
          <Button onClick={handleConfirmSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
